<script setup lang="ts">
const _props = defineProps({
  suggestions: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [],
  },
  suggestionsText: {
    type: String,
    required: false,
    default: () => '',
  },
})
// Define the types of documents to filter when fetching results.
const DOCUMENT_TYPES = ['page', 'person', 'article', 'area', 'frontpage']

const route = useRoute()

// Reactive query value from the URL
const query = ref(route.query.q || '')
// A separate ref for the debounced query
const debouncedQuery = ref(query.value)

let debounceTimeout: ReturnType<typeof setTimeout> | null = null

// Debounce the query updates: wait 300ms after the user stops typing
watch(query, (newVal) => {
  if (debounceTimeout) clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    debouncedQuery.value = newVal
  }, 300)
})

// Also update the query if the URL changes externally
watch(
  () => route.query.q,
  (newQuery) => {
    query.value = newQuery || ''
  },
)

// Reactive state for loading, errors, and results
const loading = ref(false)
const error = ref<null | string>(null)
const results = ref<unknown[]>([])
const sanity = useSanity()
const locale = useI18n().locale
// Fetch embedding results when the debounced query changes.
// The immediate option triggers this watcher on mount.
watch(
  debouncedQuery,
  async (newQuery) => {
    if (!newQuery) {
      results.value = []
      return
    }

    loading.value = true
    error.value = null

    try {
      // Get the document IDs from your embedding result function.
      const ids = await getEmbeddingResult(newQuery)

      // Query the Sanity dataset to fetch page data for the filtered document IDs.
      results.value = await sanity.fetch(getPagesFromIds, {
        documentTypes: DOCUMENT_TYPES,
        ids,
        language: locale.value,
      })
      console.log('results', results.value)
    } catch (err) {
      error.value = (err as Error).message
      console.error(err)
    } finally {
      loading.value = false
    }
  },
  { immediate: true }, // This ensures the API is called when the component mounts if there's a query.
)
const localePath = useLocalePath()
const handleSlug = (result) => {
  return result._type === 'sub-page'
    ? localePath(result.parentPage?.slug + '/' + result.slug)
    : localePath(result.slug)
}
const emit = defineEmits(['suggestion-click', 'close'])
const handleSuggestionClick = (suggestion: string) => {
  query.value = suggestion
  emit('suggestion-click', suggestion)
}
</script>

<template>
  <div class="w-full overflow-hidden text-(--white)">
    <transition name="fade" mode="out-in">
      <div v-if="loading" class="font-display mt-10 text-sm lg:mt-20 lg:text-base">Loading...</div>
      <div
        v-else-if="results.length === 0 || query === ''"
        class="mt-10 grid gap-12 lg:mt-20 lg:gap-10"
      >
        <p class="font-display text-sm lg:text-base">{{ suggestionsText }}</p>
        <ul class="tags lg:w-cols-6 inline-flex w-full flex-wrap gap-(--grid-gap)">
          <li
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="tag inline-flex cursor-pointer items-center gap-2 rounded-sm bg-(--white) px-2 py-1 text-sm text-(--green-1)"
            @click="handleSuggestionClick(suggestion)"
          >
            <IconsSearch class="!h-2.5 !w-2.5" />
            <p>{{ suggestion }}</p>
          </li>
        </ul>
      </div>
      <ul v-else class="mb-12 flex w-full flex-col overflow-auto overflow-x-hidden lg:mb-32">
        <li
          v-for="result in results"
          :key="result._id"
          class="h-full w-full border-b border-(--sand)/40 py-5 lg:py-10"
        >
          <nuxt-link
            :to="handleSlug(result)"
            :data-search-result="handleSlug(result)"
            class="flex h-full w-full flex-row items-center gap-(--grid-gap) lg:flex-row-reverse"
            @click="emit('close')"
          >
            <div class="wrapper lg:w-cols-6 ml-auto flex w-full flex-col">
              <div
                v-if="result?._type === 'article'"
                class="date mb-4 inline-flex items-center gap-2 lg:items-baseline"
              >
                <span class="text-xs uppercase">{{ result?.tag?.title }}</span>
                <IconsEllipse v-if="result?.tag?.title" class="size-1 lg:size-2" />
                <span class="text-xs uppercase">{{ formatDateString(result?.publishedAt) }}</span>
              </div>
              <h2
                class="lg:w-cols-6 font-display inline-flex w-full items-center justify-between text-lg lg:mt-0 lg:text-xl"
              >
                <span class="lg:w-cols-5 line-clamp-2 w-fit lg:line-clamp-none">
                  {{ result.title }}
                </span>
                <IconsArrowRight v-if="!result?.media" class="hidden size-5 lg:block" />
              </h2>
              <p
                v-if="result.seo?.description"
                class="lg:w-cols-5 mt-5 line-clamp-2 w-full font-sans text-sm lg:mt-7.5 lg:text-base"
              >
                {{ result.seo?.description }}
              </p>
              <!-- <pre>{{ result }}</pre> -->
            </div>
            <CommonMedia
              v-if="result?.media"
              class="lg:!w-cols-2 !w-cols-1 aspect-square h-full shrink-0 overflow-hidden rounded-sm object-cover lg:aspect-[4/3]"
              :aspect-desktop="3 / 4"
              :aspect-mobile="1"
              :media="result?.media"
            />
            <IconsArrowRight v-else class="size-5 lg:hidden" />
          </nuxt-link>
        </li>
      </ul>
    </transition>
  </div>
</template>
